
import React, { useState, useEffect } from 'react';
import processStepsData from '@/data/process-steps.json';

interface ProcessStep {
  id: string;
  step_number: number;
  title: string;
  description: string;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Default icons for each step
const getStepIcon = (stepNumber: number) => {
  const icons = {
    1: '💬',
    2: '📊', 
    3: '📈',
    4: '⚙️'
  };
  return icons[stepNumber as keyof typeof icons] || '💡';
};

const ProcessSteps = () => {
  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load static data
    const data = (processStepsData as ProcessStep[])
      .filter(s => s.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setProcessSteps(data)
    if (data.length > 0) {
      setActiveStep(data[0].step_number)
    }
    setLoading(false)
  }, []);

  const currentStep = processSteps.find(step => step.step_number === activeStep);

  if (loading) {
    return (
      <div className="py-24 bg-white">
        <div className="container-custom">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-64 mx-auto mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-slate-200 rounded-full mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-24"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="py-24 bg-white">
      <div className="container-custom max-w-6xl">
        {/* Header */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12">Process</h2>
          
          {/* Tab Navigation */}
          <div className="border-b border-slate-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
              {processSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="cursor-pointer group"
                  onClick={() => setActiveStep(step.step_number)}
                >
                  <div className="flex flex-col items-center text-center px-4">
                    {/* Icon */}
                    <div 
                      className={`text-2xl md:text-3xl mb-3 transition-all duration-300 ${
                        activeStep === step.step_number
                          ? 'scale-110'
                          : 'group-hover:scale-105'
                      }`}
                    >
                      {getStepIcon(step.step_number)}
                    </div>
                    
                    {/* Step Number and Title */}
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-slate-500">
                        {step.step_number}
                      </div>
                      <h3 
                        className={`text-xs md:text-sm font-semibold uppercase tracking-wide transition-all duration-300 ${
                          activeStep === step.step_number
                            ? 'text-blue-600'
                            : 'text-slate-700 group-hover:text-slate-900'
                        }`}
                      >
                        {step.title}
                      </h3>
                    </div>
                    
                    {/* Active underline */}
                    <div 
                      className={`h-0.5 w-full mt-4 transition-all duration-300 ${
                        activeStep === step.step_number 
                          ? 'bg-blue-600' 
                          : 'bg-transparent'
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        {currentStep && (
          <div className="transition-all duration-500 ease-in-out">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Image */}
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-50">
                  <img
                    src={currentStep.image_url || "https://images.unsplash.com/photo-1600880292203-757bb62b4baf"}
                    alt={currentStep.title}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    key={currentStep.id}
                  />
                </div>
              </div>
              
              {/* Text Content */}
              <div className="order-1 lg:order-2 space-y-6">
                <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                  {currentStep.title}
                </h3>
                <div className="prose prose-lg max-w-none">
                  <p className="text-slate-600 leading-relaxed text-lg">
                    {currentStep.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProcessSteps;
